import { useQuery } from '@tanstack/react-query';

import { apiFetch } from '@/api/client';
import type { components } from '@/api/schema';
import { usePermissions } from '@/features/auth/permissions';
import { moduleAllowed, useLicence } from '@/features/licensing/licence';

export type AiStatus = components['schemas']['AiStatus'];
export type AiSummary = components['schemas']['AiSummary'];
export type AiReport = components['schemas']['AiReport'];
export type AiReportFields = components['schemas']['AiReportFields'];
export type AiChat = components['schemas']['AiChat'];
export type AiConversation = components['schemas']['AiConversation'];
export type AiConversationDetail = components['schemas']['AiConversationDetail'];
export type AiHistoryItem = components['schemas']['AiHistory'];

export const AI_KEY = ['assistant'] as const;
export const AI_MODULE = 'IA';

/** Le module GestClinic AI est-il souscrit et l'utilisateur autorisé ? (sinon : rien à
 * l'écran, GestClinic fonctionne comme avant). */
export function useAssistantAllowed(): boolean {
  const { can } = usePermissions();
  const { data: licence } = useLicence();
  return Boolean(licence) && moduleAllowed(licence, AI_MODULE) && can('assistant.use');
}

/** État de l'IA (configurée ou non). Les boutons n'apparaissent que si elle est disponible. */
export function useAssistant() {
  const allowed = useAssistantAllowed();
  const status = useQuery({
    queryKey: [...AI_KEY, 'status'],
    queryFn: () => apiFetch<AiStatus>('/assistant/status'),
    enabled: allowed,
    staleTime: 5 * 60_000,
    retry: false,
  });
  return {
    allowed,
    ready: allowed && status.data?.available === true,
    status: status.data,
  };
}

export function sendFeedback(requestId: string, rating: 'UTILE' | 'INCORRECTE' | 'A_MODIFIER') {
  return apiFetch(`/assistant/requests/${requestId}/feedback`, {
    method: 'POST',
    body: { rating },
  });
}

export function sendOutcome(
  requestId: string,
  decision: 'ACCEPTEE' | 'MODIFIEE' | 'REJETEE',
  finalText = '',
) {
  return apiFetch(`/assistant/requests/${requestId}/outcome`, {
    method: 'POST',
    body: { decision, final_text: finalText },
  });
}
